import type { TripPhoto } from '../../types';
import { tripPhotoUrl } from '../../utils/tripPhotos';
import { Icon, MapPin } from '../ui/Icon';
import { SelectableTile } from './PhotoSelection';

interface Props {
  photos: TripPhoto[];
  onOpen: (photo: TripPhoto) => void;
}

/**
 * A day's photos, two to a row and all the same size, so one photo doesn't
 * blow up to the full width. Each carries its own stop (ADR-0042).
 */
export default function PhotoMosaic({ photos, onOpen }: Props) {
  if (photos.length === 0) return null;
  return (
    <div className="grid grid-cols-2 gap-2">
      {photos.map(photo => (
        <div key={photo.id} className="aspect-[3/2]"><Tile photo={photo} onOpen={onOpen} /></div>
      ))}
    </div>
  );
}

function Tile({ photo, onOpen }: { photo: TripPhoto; onOpen: (p: TripPhoto) => void }) {
  return (
    <SelectableTile
      photo={photo}
      onOpen={onOpen}
      style={{ height: '100%' }}
      className="relative block w-full h-full rounded-xl overflow-hidden bg-slate-100 dark:bg-slate-800 cursor-pointer group"
    >
      <img
        src={tripPhotoUrl(photo.thumbnailFilename ?? photo.filename)}
        alt={photo.originalName}
        loading="lazy"
        className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300"
      />
      {photo.activity && (
        <span className="absolute left-2 bottom-2 max-w-[calc(100%-1rem)] flex items-center gap-1 px-2 py-1 rounded-full bg-slate-900/55 text-white text-xs font-semibold backdrop-blur-sm">
          <Icon as={MapPin} size={11} className="shrink-0" />
          <span className="truncate">{photo.activity.title}</span>
        </span>
      )}
    </SelectableTile>
  );
}
